import React from 'react';
import { Container, Row, Col, Button, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaFilePdf, FaFileWord, FaFileImage, FaFile, FaExternalLinkAlt, FiChevronRight } from '../../../icons';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import ConversationAvatar from './ConversationAvatar';
import DragDropFileUpload from './DragDropFileUpload';

interface Issue {
  id?: string;
  _id?: string;
  ticketId?: string;
  subject?: string;
  title?: string;
  description: string;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Completed' | 'completed';
  submittedOn?: string;
  createdAt?: string;
  createdDate?: string;
  conversation?: Array<{
    _id?: string;
    sender: string;
    message: string;
    timestamp: string;
    attachments?: string[];
    profileUrl?: string;
    adminProfileUrl?: string;
  }>;
}

interface TicketDetailsViewProps {
  selectedIssue: Issue;
  replyText: string;
  replyFiles: FileList | null;
  onBack: () => void;
  onReplyTextChange: (text: string) => void;
  onReplyFilesChange: (files: FileList) => void;
  onReplySubmit: () => void;
}

const TicketDetailsView: React.FC<TicketDetailsViewProps> = ({
  selectedIssue,
  replyText,
  replyFiles,
  onBack,
  onReplyTextChange,
  onReplyFilesChange,
  onReplySubmit
}) => {
  const getFileInfo = (url: string, attachIndex: number) => {
    const fileName = url.split('/').pop() || `attachment-${attachIndex + 1}`;
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const fileType = extension === 'pdf' ? 'PDF' :
                  extension === 'doc' || extension === 'docx' ? 'DOC' :
                  ['jpg', 'jpeg', 'png', 'gif'].includes(extension) ? 'IMG' : 'FILE';
    return { fileName, extension, fileType };
  };

  const renderFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'PDF': return <FaFilePdf style={{ color: '#dc3545' }} />;
      case 'DOC': return <FaFileWord style={{ color: '#0d6efd' }} />;
      case 'IMG': return <FaFileImage style={{ color: '#198754' }} />;
      default: return <FaFile style={{ color: '#6c757d' }} />;
    }
  };

  return (
    <div className="help-support-page" style={{ overflowX: "hidden", width: "100%" }}>
      <Header />

      {/* Main Layout */}
      <div className="main-layout">
        <div className="gifts-body">
          <div className="gifts-body-in">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="help-support-container"
            >
              <Container fluid className='bg-white py-5'>
                {/* Breadcrumb Navigation */}
                <motion.nav
                  className="breadcrumb-navigation mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6c757d'
                  }}
                >
                  <motion.span
                    onClick={onBack}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      color: '#3957A0',
                      cursor: 'pointer',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Help & Support
                  </motion.span>

                  <FiChevronRight size={18} style={{ color: '#6c757d', marginTop: '2px' }} />

                  <span style={{ color: '#382F2D', fontWeight: '700' }}>
                    Ticket #{selectedIssue.ticketId || selectedIssue.id || selectedIssue._id}
                  </span>
                </motion.nav>

                <Row>
                  <Col lg={12} className="mx-auto">
                    <div className="issue-detail-card">
                      <div className="issue-header">
                        <h2 style={{color:'#382F2D'}}>
                          #{selectedIssue.ticketId || selectedIssue.id || selectedIssue._id}
                        </h2>

                        <div className="issue-meta d-flex flex-row justify-content-between align-items-center">
                          <div className="subject">
                            Subject: <strong>{selectedIssue.title || selectedIssue.subject}</strong>
                          </div>
                          <div className="subject">
                            Posted on: <strong>
                              {selectedIssue.createdDate ?
                                new Date(selectedIssue.createdDate).toLocaleString() :
                                selectedIssue.createdAt ?
                                new Date(selectedIssue.createdAt).toLocaleString() :
                                selectedIssue.submittedOn}
                            </strong>
                          </div>
                        </div>

                        <div className="issue-description">
                          {selectedIssue.description}
                        </div>
                      </div>

                      <div className="conversation-thread">
                        {selectedIssue.conversation?.map((message: any, index: number) => (
                          <motion.div
                            key={message._id || index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="message-item"
                          >
                            <div className="message-header">
                              <ConversationAvatar
                                author={message.sender === 'user' ? 'You' : 'Support Team'}
                                isAdmin={message.sender !== 'user'}
                                profileImage={message.sender === 'user' ? message.profileUrl : message.adminProfileUrl}
                              />
                              <div className="message-meta">
                                <strong className="author-name">
                                  {message.sender === 'user' ? 'You' : 'Support Team'}
                                </strong>
                                <span className="message-time">
                                  {new Date(message.timestamp).toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <div className="message-meta">{message.message}</div>

                            {message.attachments && message.attachments.length > 0 && (
                              <div className="message-attachments mt-3">
                                <div className="attachments-grid">
                                    {message.attachments.map((attachment: string, attachIndex: number) => {
                                      const { fileName, extension, fileType } = getFileInfo(attachment, attachIndex);

                                      return (
                                        <div key={attachIndex} className="attachment-card">
                                          <div className="attachment-icon">
                                            {renderFileIcon(fileType)}
                                          </div>
                                          <div className="attachment-info">
                                            <div className="attachment-name" title={fileName}>
                                              {fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName}
                                            </div>
                                            <div className="attachment-type">
                                              {fileType} File
                                            </div>
                                          </div>
                                          <a
                                            href={attachment}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="attachment-download-btn"
                                            title="View Attachment"
                                          >
                                            <FaExternalLinkAlt />
                                          </a>
                                        </div>
                                      );
                                    })}
                                  </div>
                              </div>
                            )}
                          </motion.div>
                        ))}
                      </div>

                      {selectedIssue.status?.toLowerCase() === 'completed' ? (
                        <div className="ticket-closed-message">
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                            style={{
                              padding: '24px',
                              borderRadius: '8px',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              textAlign: 'center'
                            }}
                          >
                            <div style={{
                              fontSize: '48px',
                              color: '#28a745',
                              marginBottom: '16px'
                            }}>
                              ✓
                            </div>
                            <h5 style={{
                              color: '#382F2D',
                              marginBottom: '12px',
                              fontWeight: '600'
                            }}>
                              Ticket Closed
                            </h5>
                            <p style={{
                              color: '#6c757d',
                              marginBottom: '0',
                              fontSize: '14px'
                            }}>
                              This ticket has been marked as completed and is now closed.
                              You cannot reply to closed tickets.
                            </p>
                          </motion.div>
                        </div>
                      ) : (
                        <div className="reply-form">
                          <h5>Reply to Ticket</h5>
                          <Form.Group className="mb-3">
                            <Form.Label>Reply *</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={4}
                              placeholder="Write your response..."
                              value={replyText}
                              onChange={(e) => onReplyTextChange(e.target.value)}
                            />
                          </Form.Group>
                          <Form.Group className="mb-3">
                            <Form.Label>Upload File</Form.Label>
                            <DragDropFileUpload
                              onFilesSelected={onReplyFilesChange}
                              currentFiles={replyFiles}
                            />
                          </Form.Group>
                          <Button
                            className="main-bttn btn-primary w-auto px-5"
                            onClick={onReplySubmit}
                            disabled={!replyText.trim()}
                          >
                            Reply
                          </Button>
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              </Container>
            </motion.div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default TicketDetailsView;